import React, { useState } from "react";
import get from "lodash/get";
import OptionToggler from "../../../parts/OptionToggler";
import ToolTip from "../../../parts/tooltip";
import Select from "../../../parts/inputs/select";
import RestTimesModal from "./RestTimesModal";
import TimeInput from "../../../parts/inputs/TimeInput";
import VelocityModal from "./VelocityModal";
import SvgIcon from "../../../parts/SvgIcon";
import submit from "submit-form";
import CollectRepMaxModal from "./CollectRepMaxModal";

const GeneralPreferences = ({
  userToSendInformation,
  roleName,
  activeUserUnits,
  startingCalendarDay,
  notAbleToEdit,
  activeUserRound,
  isFe,
  isCoach,
  userToBindInformation,
  hideStrengthMetrics,
  hideE1rm,
  intensityMeasure,
  referAsClients,
  hideVelocitySettings,
  setHideVelocitySettings,
  thresholds,
  setThresholds,
  isOrganization,
}) => {
  const unitsOptions = [
    { label: "Metric (kg)", value: "metric" },
    { label: "Imperial (lbs)", value: "imperial" },
  ];
  const preferredLoadInputOptions = [
    { label: "% of 1RM", value: "percent" },
    { label: "Weight", value: "weight" },
  ];
  const intensityMeasureOptions = [
    { label: "RPE", value: "rpe" },
    { label: "RiR", value: "rir" },
  ];

  const roundingOptions = [
    { label: "On", value: true },
    { label: "Off", value: false },
  ];

  const hideStrengthMetricsOptions = [
    { label: "Show", value: false },
    { label: "Hide", value: true },
  ];
  const hideE1rmOptions = [
    { label: "Show", value: false },
    { label: "Hide", value: true },
  ];
  const startingCalendarDayOptions = [
    { text: "Sunday", value: 0 },
    { text: "Monday", value: 1 },
    { text: "Tuesday", value: 2 },
    { text: "Wednesday", value: 3 },
    { text: "Thursday", value: 4 },
    { text: "Friday", value: 5 },
    { text: "Saturday", value: 6 },
  ];
  const [startWeekDay, setStartWeekDay] = useState(startingCalendarDay);
  const defaultTimeValue = userToBindInformation?.restTime?.default;

  return (
    <form id="form-general" method="POST" data-noreset>
      <input
        type="hidden"
        name="userToSendInformation"
        defaultValue={userToSendInformation}
      ></input>
      <input type="hidden" name="roleName" defaultValue={roleName}></input>
      <OrganizationInfoComponent
        id={"coach-level"}
        title={"Coach level"}
        body={
          "These settings will only update on your account, not the entire organization."
        }
        classnames="pb-4"
        isOrganization={isOrganization}
      />
      <div className="row first-day">
        <p className="mobile-hidden">First day in the calendar</p>
        <div className="only-show-mobile">
          <p>First day in the calendar</p>
        </div>
        <div className="action-buttons">
          <Select
            name={"startingCalendarDay"}
            options={startingCalendarDayOptions}
            value={startWeekDay}
            onChange={(newVal) => setStartWeekDay(newVal)}
          />
          <div className="no-tooltip mobile-hidden"></div>
        </div>
      </div>
      <div className="row">
        <p className="mobile-hidden">Preferred measurement system</p>
        <div className="only-show-mobile">
          <p>Preferred measurement system</p>
        </div>
        <div className="action-buttons">
          <OptionToggler
            name="units"
            options={unitsOptions}
            defaultValue={activeUserUnits}
          />
          <div className="no-tooltip mobile-hidden"></div>
        </div>
      </div>
      <OrganizationInfoComponent
        id={"organization-level"}
        title={"Organization level"}
        body={
          "These settings will update for the entire organization. They can only be configured by an admin or an owner."
        }
        classnames="py-5"
        isOrganization={isOrganization}
      />
      <div className="row">
        <p className="mobile-hidden">Rounding to Gym Increments</p>
        <div className="only-show-mobile">
          <p>Rounding to Gym Increments</p>
          <ToolTip
            id="rounding-gym-tooltip"
            body={`When loads are calculated using a %1RM the loads can be calculated as a value that cannot be loaded on a barbell. When 'on' the calculated loads are rounded to the nearest 2.5.`}
          >
            <SvgIcon
              alt="info icon"
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
        <div className="action-buttons">
          <OptionToggler
            name="round"
            options={roundingOptions}
            defaultValue={activeUserRound}
            disabled={notAbleToEdit}
          />
          <ToolTip
            className="mobile-hidden"
            id="rounding-gym-tooltip"
            body={`When loads are calculated using a %1RM the loads can be calculated as a value that cannot be loaded on a barbell. When 'on' the calculated loads are rounded to the nearest 2.5.`}
          >
            <SvgIcon
              alt="info icon"
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
      </div>
      {!isFe && !isCoach && (
        <div className="row">
          <p className="mobile-hidden">Default loading parameter</p>
          <div className="only-show-mobile">
            <p>Default loading parameter</p>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="preferredLoadInput"
              options={preferredLoadInputOptions}
              defaultValue={get(userToBindInformation, "preferredLoadInput")}
              disabled={notAbleToEdit}
            />
            <div className="no-tooltip mobile-hidden"></div>
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">
            Collect 1 rep maxes for primary lifts during user onboarding
          </p>
          <div className="only-show-mobile">
            <p>Collect 1 rep maxes for primary lifts during user onboarding</p>
          </div>
          <div className="action-buttons">
            <CollectRepMaxModal
              userToBindInformation={userToBindInformation}
              notAbleToEdit={notAbleToEdit}
            />
            <div className="no-tooltip mobile-hidden" />
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">Strength Training Metric Reports</p>
          <div className="only-show-mobile">
            <p>Strength Training Metric Reports</p>
            <ToolTip
              id="strength-training-tooltip"
              body={`These reports are strictly for advanced powerlifting training. If you are not coaching powerlifting (squat, bench press and deadlift), you can hide them from yourself and your users.`}
            >
              <SvgIcon
                alt="info icon"
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="hideStrengthMetrics"
              options={hideStrengthMetricsOptions}
              defaultValue={hideStrengthMetrics}
              disabled={notAbleToEdit}
            />
            <ToolTip
              className="mobile-hidden"
              id="strength-training-tooltip"
              body={`These reports are strictly for advanced powerlifting training. If you are not coaching powerlifting (squat, bench press and deadlift), you can hide them from yourself and your users.`}
            >
              <SvgIcon
                alt="info icon"
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">Estimated 1 rep max on completed sets</p>
          <div className="only-show-mobile">
            <p>Estimated 1 rep max on completed sets</p>
            <ToolTip
              id="one-rep-tooltip"
              body={`The system will show an estimated 1 rep max on all completed sets to show real-time strength. Our formula takes into consideration RPE/RiR to be as accurate as possible. If you do not want the users of your app to see this, you can hide it.`}
            >
              <SvgIcon
                alt="info icon"
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="hideE1rm"
              options={hideE1rmOptions}
              defaultValue={hideE1rm}
              disabled={notAbleToEdit}
            />
            <ToolTip
              className="mobile-hidden"
              id="one-rep-tooltip"
              body={`The system will show an estimated 1 rep max on all completed sets to show real-time strength. Our formula takes into consideration RPE/RiR to be as accurate as possible. If you do not want the users of your app to see this, you can hide it.`}
            >
              <SvgIcon
                alt="info icon"
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}

      <div className="row">
        <p className="mobile-hidden">Intensity Measure</p>
        <div className="only-show-mobile">
          <p>Intensity Measure</p>
          <ToolTip
            id="intensity-measure-tooltip"
            body={`Select the option to prescribe and rate load intensities as either a Rate of Perceived Exertion (RPE) or Reps in Reserve (RiR).`}
          >
            <SvgIcon
              alt="info icon"
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
        <div className="action-buttons">
          <OptionToggler
            name="intensityMeasure"
            options={intensityMeasureOptions}
            defaultValue={intensityMeasure}
            disabled={notAbleToEdit}
          />
          <ToolTip
            className="mobile-hidden"
            id="intensity-measure-tooltip"
            body={`Select the option to prescribe and rate load intensities as either a Rate of Perceived Exertion (RPE) or Reps in Reserve (RiR).`}
          >
            <SvgIcon
              alt="info icon"
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
      </div>
      {/* rest times */}
      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">Default Rest Times</p>
          <div className="only-show-mobile">
            <p>Default Rest Times</p>
          </div>
          <div className="action-buttons">
            <TimeInput
              defaultValue={defaultTimeValue}
              name={"Default"}
              notAbleToEdit={notAbleToEdit}
            />

            <RestTimesModal
              userToBindInformation={userToBindInformation}
              notAbleToEdit={notAbleToEdit}
            />
            <div className="no-tooltip mobile-hidden"></div>
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row velocity">
          <div className="velocity-title">
            <p className="mobile-hidden">
              Turn on Velocity Based Training (BETA)
            </p>
            <button
              data-button-type="text"
              onClick={(e) => {
                e.preventDefault();
                submit(`/actions/refresh/?`, {
                  method: "GET",
                  body: {
                    "modal[type]": "velocity-explanation",
                  },
                });
              }}
            >
              Learn More
            </button>
          </div>
          <div className="only-show-mobile">
            <p>Turn on Velocity Based Training (BETA)</p>
            <ToolTip
              id="hide-velocity-tooltip"
              body={`This will hide the velocity based training for your ${
                referAsClients ? "clients" : "athletes"
              }`}
            >
              <SvgIcon
                alt="info icon"
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="hideVelocitySettings"
              options={roundingOptions}
              defaultValue={!hideVelocitySettings}
              onSelectFn={(value) => {
                setHideVelocitySettings(!value);
              }}
              disabled={notAbleToEdit}
            />
            <VelocityModal
              hideVelocitySettings={hideVelocitySettings}
              thresholds={thresholds}
              setThresholds={setThresholds}
            />
            <ToolTip
              className="mobile-hidden"
              id="hide-velocity-tooltip"
              body={`Turning VBT on will enable your ${
                referAsClients ? "clients" : "athletes"
              } to track velocity for each set. VBT reports will be available in the exercise history`}
            >
              <SvgIcon
                alt="info icon"
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}
    </form>
  );
};
export default GeneralPreferences;

/**
 * Component to render the organization preference information
 * @param {*} param0
 * @returns
 */
const OrganizationInfoComponent = ({
  id,
  title,
  body,
  classnames,
  isOrganization,
}) => {
  if (!isOrganization) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${classnames}`}>
      <p className="s1">{title}</p>
      <ToolTip id={id} body={body}>
        <SvgIcon
          alt="info icon"
          className="cursor-pointer"
          src="/img/new-icons/question-circle.svg"
        />
      </ToolTip>
    </div>
  );
};
